import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  VStack,
  Grid,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useThemeColors } from "@/hooks/useThemeColors";
import { apiClient } from "@/lib/api/client";
import { useToastNotification } from "@/lib/utils/useToastNotification";
import { InvoiceSoaSummaryTable } from "@/components/forms/InvoiceSoaSummaryTable";

/** Today's date as yyyy-mm-dd (local, no conversion). */
function getTodayYmd(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** If date is ISO string, parse and return yyyy-mm-dd in local time; otherwise return as-is. */
function normalizeApiDate(dateRaw: unknown): string {
  if (dateRaw == null) return "";
  if (typeof dateRaw !== "string") return "";
  const s = dateRaw.trim();
  if (!s) return "";
  if (s.includes("T")) {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s.substring(0, 10);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  return s.length >= 10 ? s.substring(0, 10) : s;
}

export interface InvoiceFormData {
  invoice_number: string;
  date: string;
  vat: string;
}

export interface InvoiceFormProps {
  /** When provided, form is in edit mode and fetches invoice by ID. */
  invoiceId?: number;
  /**
   * Required for create mode: one or more SOA IDs for a single invoice.
   * Sent as `statement_of_account_ids` to POST /invoices/generate.
   */
  selectedSoaIds?: number[];
  /** Registered / shipping line name (create: from parent; edit: from fetched invoice). */
  registeredName?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export function InvoiceForm({
  invoiceId,
  selectedSoaIds,
  registeredName: propRegisteredName = "",
  onSuccess,
  onCancel,
}: InvoiceFormProps) {
  const colors = useThemeColors();
  const { showError, showSuccess } = useToastNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [displayRegisteredName, setDisplayRegisteredName] = useState(propRegisteredName);
  /** Edit mode: SOA IDs from fetched invoice statement_of_accounts. */
  const [editSoaIds, setEditSoaIds] = useState<number[]>([]);
  const isMountedRef = useRef(true);

  const isEditMode = !!invoiceId;
  const soaIdsForSummary = isEditMode ? editSoaIds : (selectedSoaIds ?? []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<InvoiceFormData>({
    defaultValues: {
      invoice_number: "",
      date: getTodayYmd(),
      vat: "",
    },
  });

  // Fetch invoice when in edit mode and autofill all fields from API
  useEffect(() => {
    if (!isEditMode || !invoiceId) return;
    isMountedRef.current = true;
    const fetchInvoice = async () => {
      setIsLoading(true);
      try {
        const response = await apiClient.get(`/invoices/${invoiceId}`);
        if (!isMountedRef.current) return;
        const data = response.data?.data ?? response.data ?? {};

        // Invoice number
        const invoiceNumber =
          data.invoice_number ?? (data as Record<string, unknown>).invoice_number ?? "";

        // Registered name from multi-SOA invoice response (statement_of_accounts[] / shipping_line)
        const soas =
          data.statement_of_accounts ??
          (data as Record<string, unknown>).statement_of_accounts;
        const primarySoa = Array.isArray(soas) ? soas[0] : null;
        const shippingLine =
          data.shipping_line ??
          (data as Record<string, unknown>).shipping_line ??
          primarySoa?.shipping_line ??
          (primarySoa as Record<string, unknown> | null)?.shippingLine;
        const registeredName =
          shippingLine?.name ?? (shippingLine as Record<string, unknown>)?.name ?? "";

        // Date: if ISO string, parse and show local date; otherwise display as-is
        const dateRaw = data.date ?? (data as Record<string, unknown>).date;
        const dateStr = normalizeApiDate(dateRaw);

        // Discount
        const discount = data.discount ?? (data as Record<string, unknown>).discount ?? 0;
        const vatStr =
          discount === 0 || discount === null || discount === undefined
            ? ""
            : String(discount);

        const linkedSoaIds = Array.isArray(soas)
          ? soas
              .map((soa: Record<string, unknown>) => Number(soa?.id))
              .filter((id: number) => Number.isFinite(id) && id > 0)
          : [];

        setDisplayRegisteredName(String(registeredName));
        setEditSoaIds(linkedSoaIds);
        reset({
          invoice_number: String(invoiceNumber),
          date: dateStr,
          vat: vatStr,
        });
      } catch (error: unknown) {
        if (isMountedRef.current) showError(error);
      } finally {
        if (isMountedRef.current) setIsLoading(false);
      }
    };

    fetchInvoice();
    return () => {
      isMountedRef.current = false;
    };
  }, [isEditMode, invoiceId, reset, showError]);

  const onSubmit = async (data: InvoiceFormData) => {
    const discount = data.vat === "" ? 0 : Number(data.vat);
    if (Number.isNaN(discount) || discount < 0) {
      showError("Discount must be a number >= 0");
      return;
    }

    // Send date as yyyy-mm-dd only (trim to avoid passing spaces)
    const rawDate = (data.date ?? "").trim();
    const dateYmd = rawDate ? rawDate.substring(0, 10) : rawDate;

    setIsSubmitting(true);
    try {
      if (isEditMode && invoiceId) {
        await apiClient.put(`/invoices/${invoiceId}`, {
          invoice_number: data.invoice_number.trim(),
          date: dateYmd,
          discount,
        });
        showSuccess("Success", "Invoice updated successfully");
      } else {
        const soaIds = selectedSoaIds ?? [];

        if (soaIds.length === 0) {
          showError("At least one SOA is required to create an invoice");
          return;
        }

        await apiClient.post("/invoices/generate", {
          statement_of_account_ids: soaIds,
          invoice_number: data.invoice_number.trim(),
          date: dateYmd,
          discount,
        });
        showSuccess("Success", "Invoice created successfully");
      }
      onSuccess();
    } catch (error: unknown) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" color="blue.500" />
        <Text mt={4} color={colors.text}>
          Loading invoice...
        </Text>
      </Box>
    );
  }

  return (
    <Box minW={0} w="100%" maxW="100%">
      <VStack spacing={4} align="stretch">
        {/* Details section */}
        <Card bg={colors.cardBg} borderWidth="1px" borderColor={colors.border}>
          <CardBody>
            <Box as="form" onSubmit={handleSubmit(onSubmit)} id="invoice-form">
              <VStack spacing={4} align="stretch">
                <Grid
                  templateColumns={{ base: "1fr", md: "3fr 9fr" }}
                  gap={4}
                  alignItems="start"
                >
                  <FormLabel pt={2} fontWeight="medium" color={colors.text}>
                    Registered Name: <Text as="span" color="red.500">*</Text>
                  </FormLabel>
                  <FormControl>
                    <Input
                      value={displayRegisteredName}
                      readOnly
                      bg={colors.sectionBg}
                      borderColor={colors.border}
                      color={colors.text}
                    />
                  </FormControl>
                </Grid>

                <Grid
                  templateColumns={{ base: "1fr", md: "3fr 9fr" }}
                  gap={4}
                  alignItems="start"
                >
                  <FormLabel pt={2} fontWeight="medium" color={colors.text}>
                    Invoice Number: <Text as="span" color="red.500">*</Text>
                  </FormLabel>
                  <FormControl isInvalid={!!errors.invoice_number}>
                    <Input
                      {...register("invoice_number", {
                        required: "Invoice number is required",
                        validate: (v) =>
                          (typeof v === "string" && v.trim().length > 0) ||
                          "Invoice number is required",
                      })}
                      placeholder="Input invoice number"
                      borderColor={colors.border}
                      color={colors.text}
                    />
                    <FormErrorMessage>{errors.invoice_number?.message}</FormErrorMessage>
                  </FormControl>
                </Grid>

                <Grid
                  templateColumns={{ base: "1fr", md: "3fr 9fr" }}
                  gap={4}
                  alignItems="start"
                >
                  <FormLabel pt={2} fontWeight="medium" color={colors.text}>
                    Date: <Text as="span" color="red.500">*</Text>
                  </FormLabel>
                  <FormControl isInvalid={!!errors.date}>
                    <Input
                      type="date"
                      {...register("date", { required: "Date is required" })}
                      borderColor={colors.border}
                      color={colors.text}
                    />
                    <FormErrorMessage>{errors.date?.message}</FormErrorMessage>
                  </FormControl>
                </Grid>

                <Grid
                  templateColumns={{ base: "1fr", md: "3fr 9fr" }}
                  gap={4}
                  alignItems="start"
                >
                  <FormLabel pt={2} fontWeight="medium" color={colors.text}>
                    Discount:
                  </FormLabel>
                  <FormControl isInvalid={!!errors.vat}>
                    <Input
                      type="number"
                      min={0}
                      placeholder="0.00"
                      {...register("vat", {
                        setValueAs: (v) => (v === "" ? "" : v),
                        validate: (v) => {
                          if (v === "") return true;
                          const n = Number(v);
                          if (Number.isNaN(n)) return "Must be a number";
                          if (n < 0) return "Must be 0 or greater";
                          return true;
                        },
                      })}
                      borderColor={colors.border}
                      color={colors.text}
                    />
                    <FormErrorMessage>{errors.vat?.message}</FormErrorMessage>
                  </FormControl>
                </Grid>
              </VStack>
            </Box>
          </CardBody>
        </Card>

        {/* SOA summary table */}
        <InvoiceSoaSummaryTable soaIds={soaIdsForSummary} />

        {/* Action Buttons */}
        <HStack spacing={4} justify="flex-end" pt={2}>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            borderColor={colors.border}
            color={colors.text}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="invoice-form"
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText={isEditMode ? "Updating..." : "Creating..."}
          >
            {isEditMode ? "Update" : "Create"}
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
}
